import { React, useState, useContext } from 'react';
import { useLinkMode } from '../hooks/useLinkMode.js';
import AuthContext from '../context/AuthContext';
import { FaTrash, FaSyncAlt } from 'react-icons/fa';
import { MdDeleteOutline } from 'react-icons/md';
import { updateNode } from '../api/updateNode.js';
import { deleteNode } from '../api/deleteNode.js';
import './GraphNodes.css';
import '../index.css';

const Spinner = () => <div className="spinner" />;

export const GraphNodes = ({
  nodes,
  setNodes,
  edges,
  setEdges,
  hoveredNodeId,
  setHoveredNodeId,
  hoveredLinks,
  setHoveredLinks,
  selectedNodeId,
  setSelectedNodeId,
  handleMouseDown,
  linkedNodes,
  setLinkedNodes,
  popupNodeId,
  setPopupNodeId,
  popupValue,
  setPopupValue,
  setStoredInputValue,
  isAskingAI,
  handleAskAI,
  linkMode,
  selectedEdge,
  setSelectedEdge,
}) => {
  const { commitLink, deleteLink, resetLinkMode } = useLinkMode({
    linkedNodes,
    setLinkedNodes,
    selectedEdge,
    setSelectedEdge,
    popupValue,
    setPopupValue,
    setPopupNodeId,
    setEdges,
  });
  const { authFetch } = useContext(AuthContext);
  const [editingTitleNodeId, setEditingTitleNodeId] = useState(null);
  const [editingContentNodeId, setEditingContentNodeId] = useState(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftContent, setDraftContent] = useState('');
  const [clickedNode, setClickedNode] = useState(null);
  // Helper functions to manage node logic -----------------------------------

  const handleDeleteNode = async (nodeId) => {
    try {
      const response = await deleteNode(authFetch, nodeId);

      if (response.ok) {
        setNodes((prevNodes) => prevNodes.filter((n) => n.id !== nodeId));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const storeNodeContent = async (node, draftContent) => {
    setEditingContentNodeId(null);
    if (draftContent !== node.text_content) {
      await updateNode(authFetch, node.id, {
        text_content: draftContent,
      });
      setNodes((prev) =>
        prev.map((n) =>
          n.id === node.id ? { ...n, text_content: draftContent } : n,
        ),
      );
    }
  };

  // Get transform and hover styles for nodes
  const getNodeStyles = (node) => {
    const backgroundColor = hoveredNodeId === node.id ? 'lightyellow' : 'white';
    return { backgroundColor };
  };

  // Find the hovered link for the node
  const getHoveredLink = (node) => {
    return hoveredLinks.find((link) => link.targetId === node.id);
  };

  const relatedLinks = (node, edges) => {
    return edges
      .filter((e) => e.source === node.id)
      .map((e) => ({ targetId: e.target, label: e.label }));
  };

  // onDotClick function logic for managing linked nodes
  const handleDotClick = (node) => {
    setClickedNode(node);
    setLinkedNodes((prev) => {
      if (prev.length >= 2) return prev; // Stop if already two nodes linked
      if (prev.some((n) => n.id === node.id)) return prev; // Prevent duplicate selection

      const next = [
        ...prev,
        {
          id: node.id,
          label: node.label,
          text_content: node.text_content,
        },
      ];

      // Open popup if exactly two distinct nodes are selected
      if (next.length === 2) {
        setPopupNodeId(node.id); // You could add logic here to handle popup behavior
      }

      return next;
    });
  };

  // Render title editing or normal view
  const renderNodeTitle = (node) => {
    return editingTitleNodeId === node.id ? (
      <textarea
        className="node-title-textarea"
        value={draftTitle}
        autoFocus
        onChange={(e) => setDraftTitle(e.target.value)}
        onMouseDown={(e) => e.stopPropagation()}
        onBlur={async () => {
          setEditingTitleNodeId(null);
          if (draftTitle !== node.label) {
            await updateNode(authFetch, node.id, { label: draftTitle });
            setNodes((prev) =>
              prev.map((n) =>
                n.id === node.id ? { ...n, label: draftTitle } : n,
              ),
            );
          }
        }}
      />
    ) : (
      <h4
        onDoubleClick={(e) => {
          e.stopPropagation();
          setEditingTitleNodeId(node.id);
          setDraftTitle(node.label);
        }}
      >
        {node.label}
      </h4>
    );
  };

  // Render content editing or normal view
  const renderNodeContent = (node) => {
    return editingContentNodeId === node.id ? (
      <textarea
        className="node-content-textarea"
        value={draftContent}
        autoFocus
        onChange={(e) => setDraftContent(e.target.value)}
        onMouseDown={(e) => e.stopPropagation()}
        onBlur={() => storeNodeContent(node, draftContent)}
      />
    ) : (
      <p
        onDoubleClick={(e) => {
          e.stopPropagation();
          setEditingContentNodeId(node.id);
          setDraftContent(node.text_content);
        }}
      >
        {node.text_content}
      </p>
    );
  };

  // Map through the nodes and render each node
  return nodes.map((node) => {
    const { backgroundColor } = getNodeStyles(node);
    const hoveredLink = getHoveredLink(node);
    const isTargetOfSelectedEdge =
      selectedEdge && node.id === selectedEdge.target;

    return (
      <div
        key={node.id}
        className="node-box"
        style={{
          left: node.x_axis,
          top: node.y_axis,
          border: '1px solid #bbb',
          backgroundColor,
        }}
        onMouseEnter={() => {
          setHoveredNodeId(node.id);
          setHoveredLinks(relatedLinks(node, edges));
        }}
        onMouseLeave={() => {
          setHoveredNodeId(null);
          setHoveredLinks([]);
        }}
        onMouseDown={(e) => handleMouseDown(e, node.id, node)}
        onDoubleClick={(e) => {
          e.stopPropagation();
          setSelectedNodeId((prev) => (prev === node.id ? null : node.id));
        }}
      >
        {/* Pin anchor */}
        <div className="pin-anchor" data-node-id={node.id} />

        {/* Link Labels */}
        {hoveredLink && hoveredLink.label && (
          <div className="edge-label">{hoveredLink.label}</div>
        )}

        {/* Link Mode Ring */}
        {linkMode && (
          <div
            className={`link-mode-ring ${clickedNode?.id === node.id ? 'clicked' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleDotClick(node);
            }}
            style={{
              borderColor: clickedNode?.id === node.id ? '#c0392b' : '', // Inline style example
            }}
            title="Click to link this node"
          >
            <div className="link-mode-pin" />
          </div>
        )}

        {/* Title */}
        <div className="node-title">{renderNodeTitle(node)}</div>

        {/* Content */}
        <div className="node-content">{renderNodeContent(node)}</div>

        {linkMode && hoveredNodeId === node.id && (
          <button
            className="delete-node-button"
            onClick={() => handleDeleteNode(node.id)}
          >
            <MdDeleteOutline size={25} color="black" />
          </button>
        )}

        {/* Edge Action Buttons */}
        {linkMode && isTargetOfSelectedEdge && (
          <div className="edge-action-buttons">
            <button
              className="btn-delete"
              title="Delete Link"
              onClick={deleteLink}
            >
              <FaTrash color="#fff" size={20} />
            </button>
            <button
              className="btn-update"
              title="Change Link Name"
              onClick={(e) => {
                e.stopPropagation();
                setPopupNodeId(node.id);
                setPopupValue(selectedEdge.label || '');
              }}
            >
              <FaSyncAlt color="white" size={20} />
            </button>
          </div>
        )}

        {/* Popup for link editing */}
        {popupNodeId === node.id && (
          <div
            className="link-popup-box"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <textarea
              className="link-popup-input"
              value={popupValue}
              autoFocus
              onChange={(e) => setPopupValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  commitLink();
                  setClickedNode(null);
                }
                if (e.key === 'Escape') {
                  resetLinkMode();
                  setClickedNode(null);
                }
              }}
              placeholder="Describe link"
            />
            <button className="link-popup-confirm-button" onClick={commitLink}>
              Done
            </button>
            <button
              className="link-popup-askai-button"
              disabled={isAskingAI}
              onClick={handleAskAI}
            >
              {isAskingAI ? <Spinner /> : 'Ask AI'}
            </button>
          </div>
        )}
      </div>
    );
  });
};
